"use client"

import Header from "@/components/header"
import Footer from "@/components/footer"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import {
    Mail,
    MessageSquare,
    Send,
    MapPin,
    Clock,
    Twitter,
    Github,
    MessageCircle,
    CheckCircle2,
    Loader2,
} from "lucide-react"
import { useState } from "react"
import Link from "next/link"

export function ContactContent() {
    const [formState, setFormState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "",
        category: "",
        message: "",
    })

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setFormState('sending')

        // Simulate form submission
        await new Promise(resolve => setTimeout(resolve, 2000))

        const success = Math.random() > 0.2
        if (success) {
            setFormState('success')
            setFormData({ name: "", email: "", subject: "", category: "", message: "" })
        } else {
            setFormState('error')
        }
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1 pt-40 pb-8 md:pb-12">
                <div className="container max-w-6xl">
                    {/* Header */}
                    <div className="mb-8 text-center">
                        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                            <MessageSquare className="h-8 w-8 text-primary" />
                        </div>
                        <h1 className="text-3xl font-bold tracking-tight mb-2">Contact Us</h1>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            Have questions or need assistance? We're here to help. Reach out to our team and we'll get back to you as soon as possible.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {/* Contact Form */}
                        <div className="md:col-span-2">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Send className="h-5 w-5 text-primary" />
                                        Send us a Message
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {formState === 'success' ? (
                                        <div className="py-12 text-center">
                                            <div className="h-16 w-16 rounded-full bg-[#7ed957]/15 dark:bg-[#7ed957]/20 flex items-center justify-center mx-auto mb-4">
                                                <CheckCircle2 className="h-10 w-10 text-[#6bc448]" />
                                            </div>
                                            <h3 className="text-xl font-semibold mb-2 text-[#6bc448]">Message Sent!</h3>
                                            <p className="text-muted-foreground mb-6">
                                                Thank you for contacting us. We'll get back to you within 24-48 hours.
                                            </p>
                                            <Button onClick={() => setFormState('idle')}>
                                                Send Another Message
                                            </Button>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleSubmit} className="space-y-6">
                                            <div className="grid md:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="name">Your Name *</Label>
                                                    <Input
                                                        id="name"
                                                        placeholder="John Doe"
                                                        value={formData.name}
                                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                        required
                                                        disabled={formState === 'sending'}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="email">Email Address *</Label>
                                                    <Input
                                                        id="email"
                                                        type="email"
                                                        placeholder="john@example.com"
                                                        value={formData.email}
                                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                        required
                                                        disabled={formState === 'sending'}
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid md:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="category">Category *</Label>
                                                    <Select
                                                        value={formData.category}
                                                        onValueChange={(value) => setFormData({ ...formData, category: value })}
                                                        disabled={formState === 'sending'}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select a category" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="general">General Inquiry</SelectItem>
                                                            <SelectItem value="presale">Presale Support</SelectItem>
                                                            <SelectItem value="technical">Technical Issue</SelectItem>
                                                            <SelectItem value="partnership">Partnership</SelectItem>
                                                            <SelectItem value="listing">Project Listing</SelectItem>
                                                            <SelectItem value="report">Report Scam/Fraud</SelectItem>
                                                            <SelectItem value="other">Other</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="subject">Subject *</Label>
                                                    <Input
                                                        id="subject"
                                                        placeholder="Brief description of your inquiry"
                                                        value={formData.subject}
                                                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                                        required
                                                        disabled={formState === 'sending'}
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="message">Message *</Label>
                                                <Textarea
                                                    id="message"
                                                    placeholder="Please describe your inquiry in detail..."
                                                    rows={6}
                                                    value={formData.message}
                                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                                    required
                                                    disabled={formState === 'sending'}
                                                />
                                            </div>

                                            {formState === 'error' && (
                                                <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/5">
                                                    <p className="text-sm text-red-500">
                                                        Something went wrong. Please try again.
                                                    </p>
                                                </div>
                                            )}

                                            <Button type="submit" className="w-full" disabled={formState === 'sending'}>
                                                {formState === 'sending' ? (
                                                    <>
                                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                        Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Send className="h-4 w-4 mr-2" />
                                                        Send Message
                                                    </>
                                                )}
                                            </Button>
                                        </form>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Contact Info */}
                        <div className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Contact Information</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                            <Mail className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Email</p>
                                            <a href="mailto:support@tokenkickstarter.com" className="text-sm text-muted-foreground hover:text-primary">
                                                support@tokenkickstarter.com
                                            </a>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                            <Clock className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Response Time</p>
                                            <p className="text-sm text-muted-foreground">
                                                24-48 hours
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                            <MapPin className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Location</p>
                                            <p className="text-sm text-muted-foreground">
                                                Global (Remote)
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Social Media</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <a
                                        href="https://twitter.com/tokenkickstarter"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                                    >
                                        <Twitter className="h-5 w-5 text-blue-400" />
                                        <span className="text-sm">@tokenkickstarter</span>
                                    </a>
                                    <a
                                        href="https://t.me/tokenkickstarter"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                                    >
                                        <MessageCircle className="h-5 w-5 text-blue-500" />
                                        <span className="text-sm">Telegram Community</span>
                                    </a>
                                    <a
                                        href="https://github.com/tokenkickstarter"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                                    >
                                        <Github className="h-5 w-5" />
                                        <span className="text-sm">GitHub</span>
                                    </a>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Quick Links</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <Link href="/docs" className="block text-sm text-muted-foreground hover:text-primary">
                                        📚 Documentation
                                    </Link>
                                    <Link href="/terms" className="block text-sm text-muted-foreground hover:text-primary">
                                        📜 Terms of Service
                                    </Link>
                                    <Link href="/privacy" className="block text-sm text-muted-foreground hover:text-primary">
                                        🔒 Privacy Policy
                                    </Link>
                                    <Link href="/disclaimer" className="block text-sm text-muted-foreground hover:text-primary">
                                        ⚠️ Disclaimer
                                    </Link>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </main>

            {/* SEO explainer — below the content, above footer */}
            <section className="container max-w-3xl mx-auto pb-16">
                <div className="rounded-xl bg-muted/30 p-6 text-sm text-muted-foreground space-y-3">
                    <h2 className="text-base font-semibold text-foreground">How to Reach Us</h2>
                    <p>
                        The TokenKickstarter support team is here to help with any question. Whether you need help
                        creating a presale, setting up a token lock, or troubleshooting a transaction, we respond
                        to every request within 24 to 48 hours.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>General questions</strong> — use the form above or email us directly.</li>
                        <li><strong>Technical support</strong> — include your wallet address and transaction hash for faster help.</li>
                        <li><strong>Partnership inquiries</strong> — tell us about your project and how we can work together.</li>
                        <li><strong>Bug reports</strong> — describe the issue, the page it happened on, and your browser.</li>
                    </ul>
                    <p>
                        You can also reach us on Telegram and Discord for real-time support from our community
                        and moderation team.
                    </p>
                </div>
            </section>

            <Footer />
        </div>
    )
}
